import { useEffect, useState } from 'react'
import { LogOut, Mail, Shield, UserPlus, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'
import { UserManagementModal } from './UserManagementModal'

interface UserProfileModalProps {
  open: boolean
  onClose: () => void
}

export function UserProfileModal({ open, onClose }: UserProfileModalProps) {
  const { user, logout, can } = useAuth()
  const navigate = useNavigate()
  const [loggingOut, setLoggingOut] = useState(false)
  const [userMgmtOpen, setUserMgmtOpen] = useState(false)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (userMgmtOpen) {
          setUserMgmtOpen(false)
        } else {
          onClose()
        }
      }
    }
    if (open) {
      document.addEventListener('keydown', handleKeyDown)
    }
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose, userMgmtOpen])

  if (!open || !user) return null

  const primaryRole = user.roles?.[0] ?? 'USER'
  const initials = user.full_name
    ? user.full_name
        .split(' ')
        .map((n) => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : 'U'

  const isAdmin = can('user:manage') || user.roles?.some((r) => ['ADMIN', 'SUPER_ADMIN'].includes(r))

  const handleLogout = async () => {
    try {
      setLoggingOut(true)
      await logout()
      navigate('/login')
    } finally {
      setLoggingOut(false)
      onClose()
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Modal Card */}
        <div className="relative w-full max-w-sm overflow-hidden rounded-3xl bg-white border border-slate-200/90 p-6 shadow-2xl animate-scale-in z-10">
          {/* Close Button */}
          <button
            onClick={onClose}
            aria-label="Close profile"
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800 transition active:scale-95 cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Profile Info */}
          <div className="flex flex-col items-center text-center">
            {/* Avatar */}
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-tr from-[#053736] to-emerald-600 text-white text-xl font-black mb-3.5 shadow-md ring-4 ring-emerald-50">
              {initials}
            </div>

            <h2 className="text-xl font-black text-slate-900">{user.full_name}</h2>

            <div className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-slate-500">
              <Mail className="h-3.5 w-3.5" />
              <span>{user.email}</span>
            </div>

            {/* Role Pill */}
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-800 border border-emerald-200">
              <Shield className="h-3.5 w-3.5 text-emerald-600" />
              <span>Role: {primaryRole}</span>
            </div>
          </div>

          {/* Details Divider */}
          <div className="my-5 border-t border-slate-100" />

          {/* Action Buttons */}
          <div className="space-y-2.5">
            {isAdmin && (
              <button
                onClick={() => setUserMgmtOpen(true)}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#053736] py-3 text-sm font-bold text-white shadow-sm hover:bg-[#074746] active:scale-[0.98] transition cursor-pointer"
              >
                <UserPlus className="h-4 w-4 text-emerald-400" />
                <span>Manage Team & Add Users</span>
              </button>
            )}

            <button
              onClick={handleLogout}
              disabled={loggingOut}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-rose-50 py-3 text-sm font-bold text-rose-700 border border-rose-200 hover:bg-rose-100 active:scale-[0.98] transition cursor-pointer disabled:opacity-50"
            >
              <LogOut className="h-4 w-4" />
              <span>{loggingOut ? 'Signing out...' : 'Sign out'}</span>
            </button>

            <button
              onClick={onClose}
              className="w-full py-2.5 text-xs font-bold text-slate-400 hover:text-slate-600 transition cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Embedded User Management Modal */}
      <UserManagementModal
        open={userMgmtOpen}
        onClose={() => setUserMgmtOpen(false)}
      />
    </>
  )
}
